import type { ApplicationContext } from '@hc-frontend/core-context';
import type { IOperationResult } from '@hc-frontend/core-utils-business-operation';
import { createErrorOperationResult } from '@hc-frontend/core-utils-business-operation';

import { Visit } from '../../entities';
import type { SaveVisitError, VisitRepository } from '../../repositories';
import type {
  GetJourneyProgressError,
  GetJourneyProgressUseCase,
} from '../get-journey-progress';

export type SaveVisitUseCase = (
  visitParameters: Omit<Visit, 'journeyId'>,
) => Promise<IOperationResult<void, SaveVisitError | GetJourneyProgressError>>;

export type SaveVisitFactoryParams = Pick<VisitRepository, 'saveVisit'> &
  Pick<ApplicationContext, 'log'> & {
    getJourneyProgress: GetJourneyProgressUseCase;
  };

export const saveVisitFactory =
  ({
    saveVisit,
    log,
    getJourneyProgress,
  }: SaveVisitFactoryParams): SaveVisitUseCase =>
  async (visitParameters) => {
    const journeyProgress = await getJourneyProgress({
      journeyAlias: visitParameters.journeyAlias,
    });

    if (!journeyProgress.success) return journeyProgress;

    const parsedVisitEntity = Visit.safeParse({
      ...visitParameters,
      journeyId: journeyProgress.data.id,
    });

    if (!parsedVisitEntity.success) {
      log({
        type: 'error',
        context: 'server',
        message: 'failedToSaveVisit',
        data: {
          rootCause: parsedVisitEntity.error,
        },
      });
      return createErrorOperationResult({
        errors: [
          {
            code: 'failedToSaveVisit',
            rootCause: parsedVisitEntity.error,
          },
        ],
      });
    }

    const result = await saveVisit(parsedVisitEntity.data);

    if (!result.success) {
      log({
        type: 'error',
        context: 'server',
        message: result.errors[0].code,
        data: {
          rootCause: result.errors[0].rootCause,
        },
      });
    }

    return result;
  };
